// Read settings off the LIVE gateway container app, so a bicep re-run passes the
// deployed state through instead of reconstructing it: the image it runs, the
// public-Ezra install-offer settings, and the voice relay's shared secret. Each
// reader below says what a re-run loses without it.
//
// The voice secret is the one secret here. deploy/main.bicep declares
// `voiceInternalSecret` with a `newGuid()` default, which re-evaluates on every
// deployment, so a re-run that does not pass the current value mints a new one
// and rolls BOTH the gateway and the voice relay.
//
// SECRET HYGIENE (the voice secret): the value is never logged, never written to
// a file, never put in an error message, and never stored in the
// system/infra-params row. It IS passed to `az deployment` as a bicep parameter,
// which is the point of reading it. The parameter is `@secure()` in main.bicep (so
// ARM deployment history does not carry it) and `redactAzArgs` matches its name
// (so a failed deployment does not print it), but it does sit in the local
// process argv for the length of a converge. Recorded, not hidden.
import { runAz } from "./az.js";
import { LocalCliError } from "./errors.js";

/**
 * What a live env-var read found. Three outcomes, deliberately not two:
 *
 *   • `value`      — the variable is there and this is what it holds;
 *   • `absent`     — the app genuinely has no such variable (a first deploy, or
 *                    one renamed by a newer template);
 *   • `unreadable` — we could not find out. The variable may well be there.
 *
 * `absent` and `unreadable` used to be one answer (""), and the callers' single
 * warning said "the read failed or the variable is missing" because it could not
 * tell. They are different problems with different fixes — one is expected on a
 * from-zero deploy, the other means a permission or a throttle — so they are
 * different answers here, and each caller renders its own.
 */
export type LiveEnvRead =
  | { kind: "value"; value: string }
  | { kind: "absent" }
  | { kind: "unreadable"; detail: string };

/**
 * Read one env var from the gateway container app's first container.
 *
 * TWO STEPS, because a container app env var holds its value in one of two
 * places and only one of them is readable through `containerapp show`:
 *
 *   { name, value: "…" }                 → a plain value, right there;
 *   { name, secretRef: "the-secret" }    → the value lives in the app's secret
 *                                          collection and the entry carries NO
 *                                          `value` key at all.
 *
 * This used to project `.value` straight out of the entry, which returned empty
 * for every secretRef-backed variable — including M8T_INTERNAL_SECRET, which is
 * secretRef-backed on every install. The caller read that empty string as "there
 * is nothing to preserve", so main.bicep's `newGuid()` default minted a fresh
 * shared secret and rolled BOTH the gateway and the voice relay, on EVERY
 * converge. The compensation existed, ran, and was structurally incapable of
 * working. Measured on a live gateway 2026-08-11: the entry is exactly
 * `{"name":"M8T_INTERNAL_SECRET","secretRef":"voice-internal-secret"}`.
 *
 * The second step (`containerapp secret show`) is a data call and needs
 * `Microsoft.App/containerApps/listSecrets/action`. Verified live rather than
 * assumed: the founder runs as subscription Owner, and the deployed updater
 * managed identity — the other principal that converges — holds Owner on the
 * resource group. Both carry `*`.
 *
 * SECRET HYGIENE: the value is returned to the caller and nothing else. It is
 * never logged, never written to a file, never put in an error message.
 */
export async function readLiveGatewayEnvVar(opts: {
  gatewayName: string;
  resourceGroup: string;
  subscriptionId?: string;
  name: string;
}): Promise<LiveEnvRead> {
  const show = ["containerapp", "show", "-n", opts.gatewayName, "-g", opts.resourceGroup];
  if (opts.subscriptionId) show.push("--subscription", opts.subscriptionId);
  // The whole ENTRY, not a projection of one of its fields: which field holds
  // the value is the question, so the answer cannot be baked into the query.
  show.push("--query", `properties.template.containers[0].env[?name=='${opts.name}'] | [0]`, "-o", "json");
  let raw: string;
  try {
    raw = await runAz(show);
  } catch (e) {
    return { kind: "unreadable", detail: (e as Error).message };
  }
  const text = raw.trim();
  // An empty projection prints NOTHING on the az in the field — not the literal
  // `null` a JSON reader would expect. Measured, not assumed: parsing "" would
  // have reported a plainly absent variable as an unreadable one, which is the
  // exact conflation this function exists to end.
  if (text === "" || text === "null" || text === "None") return { kind: "absent" };
  let entry: { value?: unknown; secretRef?: unknown };
  try {
    entry = JSON.parse(text) as { value?: unknown; secretRef?: unknown };
  } catch {
    // The parser's own message quotes the first bytes of what it was given. Those
    // bytes are the start of a container-app env entry, not a secret — but this
    // file promises the value never reaches an error message, and a promise that
    // holds only because of a byte offset is not one. The message is dropped.
    return { kind: "unreadable", detail: "the env entry did not parse as JSON" };
  }
  // A value-backed entry answers itself. Legacy installs are value-backed, and
  // they must keep working without a second az call.
  if (typeof entry.value === "string" && entry.value !== "") {
    return { kind: "value", value: entry.value };
  }
  if (typeof entry.secretRef !== "string" || entry.secretRef === "") {
    // The variable exists but holds neither a value nor a reference. Nothing to
    // preserve — the same answer as no variable at all, and not a failure.
    return { kind: "absent" };
  }
  const secret = ["containerapp", "secret", "show", "-n", opts.gatewayName, "-g", opts.resourceGroup];
  if (opts.subscriptionId) secret.push("--subscription", opts.subscriptionId);
  secret.push("--secret-name", entry.secretRef, "--query", "value", "-o", "tsv");
  let secretRaw: string;
  try {
    secretRaw = await runAz(secret);
  } catch (e) {
    // Named so the remedy is obvious: the variable IS there, we were refused the
    // one call that reads it. Reporting `absent` here would tell the caller there
    // is nothing to preserve, which is how a re-mint gets justified.
    return {
      kind: "unreadable",
      detail: `could not read secret '${entry.secretRef}': ${(e as Error).message}`,
    };
  }
  const value = secretRaw.trim();
  // `az … -o tsv` prints the literal `None` for a null scalar on some versions.
  if (value === "" || value === "None") {
    return { kind: "unreadable", detail: `secret '${entry.secretRef}' read back empty` };
  }
  return { kind: "value", value };
}

/**
 * The image the gateway container app is ACTUALLY running: `{ image }`, with ""
 * when the app reports no image, or `{ error }` when the read fails, which the
 * caller refuses on rather than treating as nothing to preserve
 * (resolveBicepParamsForConverge in platform-converge.ts).
 *
 * An infra converge must not re-point the gateway at the manifest's image. The
 * gateway roll is `applyGatewayImage`'s job, and it deliberately REFUSES a
 * BYOC/private-ACR gateway (marking it externally managed) rather than dragging
 * it onto the tracked public repo. Passing the manifest's ref into bicep
 * bypassed that refusal — and on any install whose container app authenticates
 * to its registry with a managed identity, Azure rejects the deployment
 * outright: ContainerAppRegistryServerNotAllowedForManagedIdentity, "the
 * registry server 'ghcr.io' is not allowed when using managed identities".
 * Live-proven on m8t's own canary, 2026-08-11.
 */
export async function readLiveGatewayImage(opts: {
  gatewayName: string;
  resourceGroup: string;
  subscriptionId?: string;
}): Promise<{ image: string } | { error: string }> {
  const args = ["containerapp", "show", "-n", opts.gatewayName, "-g", opts.resourceGroup];
  if (opts.subscriptionId) args.push("--subscription", opts.subscriptionId);
  args.push("--query", "properties.template.containers[0].image", "-o", "tsv");
  try {
    const v = (await runAz(args)).trim();
    // `az … -o tsv` prints the literal `None` for a null scalar on some versions.
    return { image: v === "None" ? "" : v };
  } catch (e) {
    // Not swallowed to "": "" means the app reports no image, and the caller
    // must tell that apart from a failed read — guessing an image on a failed
    // read re-points a live gateway.
    return { error: (e as Error).message };
  }
}

/** The public-Ezra install-offer settings, as bicep parameters. */
export interface LiveInstallOfferParams {
  publicEzraInstallOffers?: string;
  publicEzraInstallOfferCanaryAgent?: string;
}

/**
 * The public-Ezra install-offer settings, read off the live gateway so a bicep
 * deploy re-emits them. They are set on the one gateway that serves the public
 * Ezra relay and recorded nowhere else, and a deploy replaces the gateway's
 * whole env list: the platform 0.8.12 update dropped PUBLIC_EZRA_INSTALL_OFFERS
 * that way. A variable the app lacks is left out; a read that fails throws, so
 * the deploy stops instead of dropping the setting again.
 */
export async function readLiveInstallOfferParams(opts: {
  gatewayName: string;
  resourceGroup: string;
  subscriptionId?: string;
}): Promise<LiveInstallOfferParams> {
  const params: LiveInstallOfferParams = {};
  for (const [field, name] of [
    ["publicEzraInstallOffers", "PUBLIC_EZRA_INSTALL_OFFERS"],
    ["publicEzraInstallOfferCanaryAgent", "PUBLIC_EZRA_INSTALL_OFFER_CANARY_AGENT"],
  ] as const) {
    const read = await readLiveGatewayEnvVar({ ...opts, name });
    if (read.kind === "unreadable") {
      throw new LocalCliError({
        code: "PLATFORM_GATEWAY_ENV_UNREADABLE",
        message: `Could not read ${name} from '${opts.gatewayName}' in ${opts.resourceGroup}: ${read.detail}`,
        hint: "This deploy re-renders the gateway's settings and must not drop this one. Re-run it; if this persists, check the running identity's read access to the container app.",
      });
    }
    if (read.kind === "value") params[field] = read.value;
  }
  return params;
}

/** The voice relay ↔ gateway shared secret, read off live state. Never log the result. */
export function readLiveVoiceInternalSecret(opts: {
  gatewayName: string;
  resourceGroup: string;
  subscriptionId?: string;
}): Promise<LiveEnvRead> {
  return readLiveGatewayEnvVar({ ...opts, name: "M8T_INTERNAL_SECRET" });
}

/**
 * The value to pass to bicep, and the sentence to say about it — the shape both
 * converge paths need, so neither has to re-derive the rule.
 *
 * "" means "let the template's newGuid() default mint one", which is correct on a
 * from-zero deploy and a re-mint anywhere else. So an UNREADABLE read still
 * yields "": we fail toward minting a working secret, never toward passing a
 * guess that would leave the relay unable to talk to the gateway. The warning is
 * what makes that honest — it is the only signal that a roll is about to happen
 * for a reason nobody chose.
 */
export function voiceSecretParam(read: LiveEnvRead, gatewayName: string): { value: string; warning?: string } {
  if (read.kind === "value") return { value: read.value };
  if (read.kind === "absent") {
    return {
      value: "",
      warning:
        `gateway '${gatewayName}' has no M8T_INTERNAL_SECRET to preserve, so this converge will mint one, ` +
        "rolling BOTH the gateway and the voice relay. Expected on a first deploy.",
    };
  }
  return {
    value: "",
    warning:
      `could not read the voice-relay secret (M8T_INTERNAL_SECRET) off gateway '${gatewayName}': ${read.detail}. ` +
      "The variable may well be set — this converge will mint a NEW secret anyway, rolling BOTH the gateway and the voice relay.",
  };
}
